import isEmpty from 'lodash/isEmpty';
import { useAppendDraftComment } from 'pages/WellGuardian/WIR/hooks';
import {
  getFinalWirReactiveScopeData,
  getWirCreatedReactiveScopeData,
} from 'pages/WellGuardian/WIR/utils';
import {
  WIRCreateJourneyStepsKeys,
  WIRFinalWIRSections,
  WIRStageGate3Sections,
} from 'pages/WellGuardian/WIR/wir.constants';
import { useCallback, useMemo } from 'react';
import { useCountryState, useSelectedWIR } from '../../../../redux/WellGuardian/selectors';
import wellGuardianApiClient from 'services/networking/wellGuardianApiClient';
import { WIR, WIRPhaseTypes } from 'types/wellGuardian';

import { getFinalWirPhaseName } from '../utils/getSectionData';

export const useSectionManagerialSummaryData = () => {
  const wirFromState = useSelectedWIR();
  const { appendDraftComment } = useAppendDraftComment();
  const countryState = useCountryState();

  const managerialSummarySectionPayload = useMemo(() => {
    const { phase, managerialSummary } = getFinalWirReactiveScopeData(wirFromState, countryState);
    const { phase: phaseWirCreatedOrRs } = getWirCreatedReactiveScopeData(
      wirFromState,
      countryState,
    );
    const shortFalls = managerialSummary?.shortFalls;
    const shortFall = managerialSummary?.shortFall;
    const interventionDuration = managerialSummary?.interventionDuration;
    const posOfGains = managerialSummary?.posOfGains;
    const boePriceAssumed = managerialSummary?.boePriceAssumed;
    const afeCost = managerialSummary?.afeCost;
    const technicalPos = managerialSummary?.technicalPos;
    const payBack = managerialSummary?.payBack;
    const ghgEmission = managerialSummary?.ghgEmission;
    const comments = appendDraftComment(managerialSummary?.comments || []);
    const productionGains =
      managerialSummary?.productionGains ??
      (wirFromState?.phases?.[phaseWirCreatedOrRs]?.sections?.initialRequestInformation
        ?.productGains ||
        '');
    const productionSafeguarded =
      managerialSummary?.productionSafeguarded ??
      (wirFromState?.phases?.[phaseWirCreatedOrRs]?.sections?.initialRequestInformation
        ?.productionSafeguarded ||
        '');
    const sectionCompleteConfirmation = managerialSummary?.sectionCompleteConfirmation;

    const isTicked =
      !isEmpty(shortFalls) &&
      !isEmpty(shortFall) &&
      !isEmpty(interventionDuration) &&
      !isEmpty(posOfGains) &&
      !isEmpty(boePriceAssumed) &&
      !isEmpty(afeCost) &&
      !isEmpty(technicalPos) &&
      !isEmpty(payBack) &&
      !isEmpty(ghgEmission) &&
      !isEmpty(productionGains) &&
      !isEmpty(productionSafeguarded);

    return {
      shortFalls,
      isTicked,
      shortFall,
      interventionDuration,
      posOfGains,
      boePriceAssumed,
      afeCost,
      technicalPos,
      payBack,
      ghgEmission,
      productionGains,
      productionSafeguarded,
      sectionCompleteConfirmation,
      comments,
      phase,
      lastUpdatedDate: new Date().toISOString(),
      assignedTo: managerialSummary?.assignedTo || '',
    };
  }, [wirFromState, appendDraftComment, countryState]);

  const saveManagerialSummarySection = useCallback(
    async (section: string, processSaveResponse: (wirResponse: WIR | null) => void) => {
      const phaseName = getFinalWirPhaseName(countryState);
      if (
        wirFromState?.wirNumber &&
        (section === WIRStageGate3Sections.managerialSummary ||
          section === WIRFinalWIRSections.managerialSummary)
      ) {
        processSaveResponse(
          await wellGuardianApiClient.updateWIRManagerialSummarySection(
            wirFromState?.wirNumber,
            managerialSummarySectionPayload,
            managerialSummarySectionPayload.phase === WIRCreateJourneyStepsKeys.REACTIVE_SCOPE
              ? WIRPhaseTypes.REACTIVE_SCOPE
              : phaseName,
            countryState,
          ),
        );
      }
    },
    [wirFromState, managerialSummarySectionPayload, countryState],
  );

  return {
    saveManagerialSummarySection,
    managerialSummarySectionPayload,
  };
};
